import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { X } from "lucide-react";
import { SpinnerCircularFixed } from "spinners-react";
import { AuthContext } from "../../App";
import { BusinessContext } from "../BusinessProvider/BusinessProvider";
import { AlertsModal, AlertsModalProps, AlertType } from "./interfaces";

export const ErrorModalContext = createContext<AlertsModal>({
  showAlert: () => null,
  hideAlert: () => null,
});

export default function AlertModal({ children }: AlertsModalProps) {
  const { getLogout } = useContext(AuthContext);
  const { logoutConcession } = useContext(BusinessContext);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [text, setText] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [type, setType] = useState<AlertType | null>(null);
  const timerRef = useRef<NodeJS.Timeout>();

  const showAlert = (
    alertText: string,
    alertType: AlertType = "error",
    alertTitle = "",
  ): void => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setShowModal(true);
    setText(alertText);
    setType(alertType);
    setTitle(alertTitle);
    timerRef.current = setTimeout(() => {
      hideAlert();
    }, 5000);
  };

  const hideAlert = (): void => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setShowModal(false);
    setText("");
    setTitle("");
    setType(null);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (text === "sesión expirada") {
      getLogout();
      logoutConcession();
    }
  }, [text]);

  return (
    <ErrorModalContext.Provider value={{ showAlert, hideAlert }}>
      {type && (
        <div
          className={`absolute w-[calc(100%-2rem)] ${
            showModal
              ? "top-4 md:bottom-4 md:top-auto lg:bottom-4 lg:right-0"
              : "-top-[2000px]"
          } ${
            type === "error"
              ? "bg-secondary text-white"
              : type === "warning"
                ? "border border-yellow-300 bg-yellow-50 text-yellow-800"
              : "border border-gray-200 bg-white text-gray-800"
          } left-4 z-[9999] flex flex-row items-center gap-3 rounded-md p-4 shadow-md transition-all md:left-1/2 md:-translate-x-1/2 lg:left-auto lg:right-4 lg:max-w-md lg:translate-x-0`}
        >
          {type !== "loading" ? (
            <X
              className="absolute right-2 top-2 shrink-0 cursor-pointer"
              size={18}
              onClick={hideAlert}
            />
          ) : (
            <SpinnerCircularFixed
              size={20}
              thickness={100}
              speed={250}
              color="rgba(215, 1, 90, 1)"
              secondaryColor="rgba(241, 241, 241, 1)"
            />
          )}
          <div className="flex flex-col gap-0.5">
            {title && <span className="text-sm font-bold">{title}</span>}
            <span className="text-sm">{text}</span>
          </div>
        </div>
      )}
      {children}
    </ErrorModalContext.Provider>
  );
}
